import React from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import ApiService from "../services/api";
import { cookieHelpers } from "../utils/cookieUtils";
import { isTokenExpired } from "../utils/tokenUtils";
import { createSession } from "../components/SessionProvider";
import { VIEWS } from "../constants/views";
import { useSessionTracking } from "./hooks/useSessionTracking";

// Import pages
import { ContactPage } from "../pages/customer/ContactPage";
import UserInfoPage from "../pages/user/UserInfoPage";
import EditProfilePage from "../pages/user/EditProfilePage";
import SelfieCapturePage from "../pages/analysis/SelfieCapturePage";
import ScoresPage from "../pages/analysis/ScoresPage";
import FeedbackPage from "../pages/analysis/FeedbackPage";
import ErrorPage from "../pages/ErrorPage";
import LandingPage from "../pages/customer/LandingPage";
import Welcome from "../pages/customer/WelcomePage";
import MoreUserDetails from "../pages/more-user-details";
import UserDetails from "../pages/user/UserDetails";
import DistributorLoginPage from "../pages/distributor/DistributorLoginPage";
import PrivacyPoliciesPage from "../pages/PrivacyPoliciesPage";

const CustomerFlow: React.FC = () => {
  const {
    currentView,
    sessionId,
    setDistributorId,
    setCurrentView,
    setToken,
    setSessionId,
    setSessionError,
    distributorId,
    fetchUserProfiles,
    role,
    setRole,
    currentLanguage,
    setSnackbar,
    getTranslation,
  } = useAppContext();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Track session view changes
  useSessionTracking(currentView, sessionId);

  React.useEffect(() => {
    const apiService = new ApiService();
    const distributorId = apiService.getDistributorIdFromUrl();

    if (distributorId) {
      setDistributorId(distributorId);
    }
  }, [searchParams, setDistributorId]);

  React.useEffect(() => {
    const checkTokenAndRedirect = async () => {
      const storedToken = cookieHelpers.getToken('customer');
      const storedUserId = cookieHelpers.getUserId('customer');
      const language = currentLanguage;
      if (storedToken && storedUserId) {
        try {
          const api = new ApiService(null, storedToken);
          const userProfiles = (await fetchUserProfiles(
            api,
            role || undefined
          )) as any;

          let extractedDistributorId: string | null = distributorId;
          if (userProfiles && userProfiles.user) {
            extractedDistributorId = userProfiles.user.user.distributor_id;
          }

          setToken(storedToken);

          if (!sessionId) {
            try {
              await createSession(
                setSessionId,
                setSessionError,
                language || "en",
                storedUserId,
                setRole,
                undefined,
                extractedDistributorId || undefined
              );
              setCurrentView(VIEWS.WELCOME);
            } catch (sessionError) {
              console.error("Session creation failed:", sessionError);
              setSnackbar({
                open: true,
                severity: "error",
                message:
                  (typeof sessionError === "string" && sessionError) ||
                  (sessionError instanceof Error && sessionError.message) ||
                  getTranslation(
                    "Err95",
                    "Failed to create session. Please try again."
                  ),
              });
            }
          } else {
            setCurrentView(VIEWS.WELCOME);
          }
        } catch (error) {
          console.error("Token validation failed:", error);
          cookieHelpers.removeToken();
          cookieHelpers.removeUserId();
          setToken(null);
          setCurrentView(VIEWS.HOME);
        }
      }
    };

    checkTokenAndRedirect();
  }, [
    location.pathname,
    sessionId,
    setToken,
    setCurrentView,
    setSessionId,
    setSessionError,
    distributorId,
    fetchUserProfiles,
    role,
  ]);

  React.useEffect(() => {
    const isCustomerFlow = window.location.pathname.startsWith("/customer");
    const storedToken = cookieHelpers.getToken('customer');
    if (storedToken && isCustomerFlow && !isTokenExpired(storedToken)) {
      setCurrentView(VIEWS.WELCOME);
    }
  }, []);

  const renderView = () => {
    switch (currentView) {
      case VIEWS.HOME:
        return <LandingPage />;
      case VIEWS.CONTACT:
        return <ContactPage />;
      case VIEWS.FORGOT_PASSWORD:
        return <ContactPage />;
      case VIEWS.SIGNUP:
        return <ContactPage />;
      case VIEWS.USER_INFO:
        return <UserInfoPage />;
      case VIEWS.EDIT_PROFILE:
        return <EditProfilePage />;
      case VIEWS.SELFIE:
        return <SelfieCapturePage />;
      case VIEWS.SCORES:
        return <ScoresPage />;
      case VIEWS.FEEDBACK:
        return <FeedbackPage />;
      case VIEWS.ERROR:
        return <ErrorPage />;
      case VIEWS.WELCOME:
        return <Welcome />;
      case VIEWS.MORE_USER_DETAILS:
        return <MoreUserDetails />;
      case VIEWS.USER_DETAILS:
        return <UserDetails />;
      case VIEWS.DISTRIBUTOR_LOGIN:
        return <DistributorLoginPage />;
      case VIEWS.PRIVACY_POLICY:
        return <PrivacyPoliciesPage />;
      default:
        return <LandingPage />;
    }
  };

  return renderView();
};

export default CustomerFlow;
